/**
 * Which build is on the screen.
 *
 * A PWA updates behind you: the service worker serves the shell from cache and revalidates
 * afterwards, so the app you are looking at can be a deploy or two behind the one you just
 * published, and nothing on screen says so. Answering "is this the new one" by reading the
 * bundle hash out of devtools is not something to ask of anybody, and `curl` has already
 * lied about a deploy once in this project — a header check is not evidence that a browser
 * is running the new code.
 *
 * So the commit is stamped into the bundle at build time and printed in Settings. It
 * describes the JavaScript that is executing, not the newest commit in the repository,
 * which is exactly what makes it worth reading.
 */

import { isNativeShell } from '../config/origin';

/** Injected by vite `define`. Absent under vitest, which does not go through the build. */
declare const __BUILD_COMMIT__: string;
declare const __BUILD_COMMIT_ISO__: string;
declare const __BUILD_DIRTY__: boolean;
declare const __BUILD_NUMBER__: number;

function value<T>(read: () => T, fallback: T): T {
  try {
    return read();
  } catch {
    // A test importing this module has no `define` substitution, and a ReferenceError
    // here would take the screen down over a version string.
    return fallback;
  }
}

export const BUILD_COMMIT = value(() => __BUILD_COMMIT__, 'unknown');
export const BUILD_COMMIT_ISO = value(() => __BUILD_COMMIT_ISO__, '');
export const BUILD_DIRTY = value(() => __BUILD_DIRTY__, false);
export const BUILD_NUMBER = value(() => __BUILD_NUMBER__, 0);

/**
 * Which shell is on the screen. Half of "which build am I running", and the half a
 * commit hash cannot answer.
 *
 * Three states rather than two, because they go stale for three different reasons. `app`
 * is the native shell, whose page is bundled inside the APK and therefore frozen until
 * somebody installs a new one. `pwa` is an installed web app, which is *resumed* rather
 * than loaded, so it can sit whole deploys behind while looking perfectly healthy. `web`
 * is a browser tab, which is the only one of the three that is current by default.
 */
export type Surface = 'app' | 'pwa' | 'web';

function standaloneDisplay(): boolean {
  try {
    return typeof matchMedia === 'function'
      && matchMedia('(display-mode: standalone)').matches;
  } catch {
    return false;
  }
}

export function currentSurface(
  native: boolean = isNativeShell(),
  standalone: boolean = standaloneDisplay(),
): Surface {
  if (native) return 'app';
  return standalone ? 'pwa' : 'web';
}

/**
 * The watermark: `#98 · web`, or `#98+ · app` when the tree had edits in it.
 *
 * Short enough to read without looking for it, which is the whole specification. The
 * number is the web build rather than the Android `versionCode`, and on the native shell
 * that is deliberately the more useful of the two: the planner, the withheld list and
 * every screen ship inside this bundle, so it is the number that says whether a fix is
 * actually present on the device.
 */
export function buildMark(
  build: number = BUILD_NUMBER,
  surface: Surface = currentSurface(),
  dirty: boolean = BUILD_DIRTY,
): string {
  return `#${build}${dirty ? '+' : ''} · ${surface}`;
}

/**
 * `2026-08-21 03:35` in the reader's own zone, from the commit's ISO timestamp.
 *
 * Local rather than UTC on purpose: the question this answers is "is this newer than the
 * change I made an hour ago", and that is asked in the time on the reader's own clock.
 */
export function formatBuildDate(iso: string, now: Date = new Date()): string {
  if (!iso) return 'date unknown';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return 'date unknown';
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`
    + ` ${pad(at.getHours())}:${pad(at.getMinutes())}`;
  const days = Math.floor((now.getTime() - at.getTime()) / 86_400_000);
  if (days < 0) return stamp;
  if (days === 0) return `${stamp} · today`;
  if (days === 1) return `${stamp} · yesterday`;
  return `${stamp} · ${days} days ago`;
}

/**
 * The one line Settings prints.
 *
 * The build number leads it, so the line and the watermark on the home screen are
 * obviously the same fact at two levels of detail; the commit follows it for anyone who
 * needs to look the build up in the repository.
 */
export function buildStamp(
  build: number = BUILD_NUMBER,
  commit: string = BUILD_COMMIT,
  iso: string = BUILD_COMMIT_ISO,
  dirty: boolean = BUILD_DIRTY,
  now: Date = new Date(),
): string {
  const mark = dirty ? `${commit}+local` : commit;
  return `Build #${build} · ${mark} · ${formatBuildDate(iso, now)}`;
}
